import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, MapPin, Users, Trophy, Radio, ChevronRight, ChevronLeft, Gamepad2 } from "lucide-react";
import api, { LOGO_WARZONE } from "@/lib/api";
import { IMG } from "@/lib/assets";

function fmtDate(iso) {
  if (!iso) return "Da definire";
  const d = new Date(iso);
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
}
function fmtTime(iso) {
  if (!iso) return "--:--";
  return new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
}

function TournamentCard({ t, accent = "cyan" }) {
  const gold = accent === "gold";
  return (
    <Link to={`/tornei/${t.id}`} data-testid={`tour-card-${t.id}`}
      className={`block p-6 clip-hud transition-all hover:-translate-y-1 ${gold ? "panel panel-gold hover:shadow-[0_0_24px_rgba(201,162,39,0.25)]" : "cyber-panel"}`}>
      <div className="flex items-center justify-between mb-3">
        <span className={`font-rajdhani uppercase tracking-wider text-[10px] px-2 py-1 clip-beveled-sm ${gold ? "bg-[var(--gold)] text-black" : "bg-[#22d3ee] text-black"}`}>{t.mode}</span>
        <span className="font-rajdhani text-gray-400 text-xs uppercase">{t.season}</span>
      </div>
      <h3 className={`heading text-3xl ${gold ? "gold-text" : "cyber-text"}`}>{t.name}</h3>
      {t.subtitle && <p className="font-rajdhani text-gray-400 text-sm">{t.subtitle}</p>}
      <div className="grid grid-cols-2 gap-3 mt-4 font-rajdhani text-sm text-gray-300">
        <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-gray-500" />{fmtDate(t.start_at)}</span>
        <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-gray-500" />{fmtTime(t.start_at)}</span>
        <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-gray-500" />{t.map}</span>
        <span className="flex items-center gap-2"><Users className="w-4 h-4 text-gray-500" />{t.teams}/{t.max_teams} team</span>
      </div>
      <div className={`flex items-center justify-end gap-1 mt-4 font-rajdhani uppercase text-xs tracking-wider ${gold ? "text-[var(--gold-light)]" : "text-[#67e8f9]"}`}>Dettagli <ChevronRight className="w-4 h-4" /></div>
    </Link>
  );
}

const PER_PAGE = 3;

export default function PubTornei() {
  const [data, setData] = useState({ active: [], upcoming: [], past: [] });
  const [season, setSeason] = useState("all");
  const [page, setPage] = useState(0);

  useEffect(() => { api.get("/public/tournaments").then(({ data }) => setData(data)).catch(() => {}); }, []);

  const seasons = useMemo(() => {
    const s = new Set((data.past || []).map((t) => t.season).filter(Boolean));
    return Array.from(s);
  }, [data.past]);

  const filteredPast = useMemo(() => {
    if (season === "all") return data.past || [];
    return (data.past || []).filter((t) => t.season === season);
  }, [data.past, season]);

  useEffect(() => { setPage(0); }, [season]);

  const totalPages = Math.max(1, Math.ceil(filteredPast.length / PER_PAGE));
  const currentPage = Math.min(page, totalPages - 1);
  const pageItems = filteredPast.slice(currentPage * PER_PAGE, currentPage * PER_PAGE + PER_PAGE);

  return (
    <div className="text-white">
      {/* HERO fusion */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: `url(${IMG.cyberBg})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-[#0b1a34]/80 to-black" />
        <div className="absolute inset-0 grid-overlay opacity-30" />
        <div className="relative max-w-7xl mx-auto px-5 text-center">
          <span className="inline-flex items-center gap-2 font-rajdhani uppercase tracking-[0.3em] text-[#67e8f9] text-xs mb-4"><Gamepad2 className="w-4 h-4" /> Non fartelo raccontare...</span>
          <h1 className="heading text-5xl sm:text-7xl lg:text-8xl"><span className="cyber-text">TOR</span><span className="gold-text">NEI</span></h1>
          <p className="font-rajdhani text-gray-300 text-lg mt-4 max-w-2xl mx-auto">Seals Allies Force Calendar: Tornei in corso, Prossimi Tornei e Tornei Passati.</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 py-16 space-y-20">
        {/* ATTIVO */}
        <section data-testid="section-active">
          <div className="flex items-center gap-3 mb-6"><Radio className="w-5 h-5 state-live animate-pulse" /><h2 className="heading text-4xl text-white">Torneo <span className="state-live">In Corso</span></h2></div>
          {data.active.length ? (
            <div className="grid md:grid-cols-2 gap-6">
              {data.active.map((t) => (
                <div key={t.id} className="cyber-panel clip-hud p-8 relative overflow-hidden md:col-span-2" data-testid={`active-${t.id}`}>
                  <div className="absolute inset-0 grid-overlay opacity-20" />
                  <div className="relative flex flex-col md:flex-row gap-6 items-center">
                    <img src={LOGO_WARZONE} alt="" className="w-28 h-28 object-contain drop-shadow-[0_0_30px_rgba(34,211,238,0.5)]" />
                    <div className="flex-1 text-center md:text-left">
                      <span className="font-rajdhani uppercase tracking-wider text-[10px] state-live border border-[#10b981]/40 px-2 py-1">● LIVE</span>
                      <h3 className="heading text-5xl cyber-text mt-2">{t.name}</h3>
                      <p className="heading text-xl text-gray-300">{t.subtitle}</p>
                      <div className="flex flex-wrap gap-4 mt-3 font-rajdhani text-sm text-gray-300 justify-center md:justify-start">
                        <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{t.map}</span>
                        <span className="flex items-center gap-1"><Users className="w-4 h-4" />{t.teams}/{t.max_teams} team</span>
                        <span className="flex items-center gap-1"><Gamepad2 className="w-4 h-4" />{t.mode}</span>
                      </div>
                    </div>
                    <Link to={`/tornei/${t.id}`} className="btn-cyber clip-hud px-8 py-3 shrink-0">Visualizza Torneo</Link>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="cyber-panel clip-hud p-8 text-center font-rajdhani text-gray-400" data-testid="no-active">Nessun torneo attualmente in corso.</p>}
        </section>

        {/* PROSSIMI */}
        <section data-testid="section-upcoming">
          <div className="flex items-center gap-3 mb-6"><Calendar className="w-5 h-5 text-[#67e8f9]" /><h2 className="heading text-4xl text-white">Prossimi <span className="cyber-text">Tornei</span></h2></div>
          {data.upcoming.length ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">{data.upcoming.map((t) => <TournamentCard key={t.id} t={t} accent="cyan" />)}</div>
          ) : <p className="font-rajdhani text-gray-500">Nessun torneo in programma al momento.</p>}
        </section>

        {/* PASSATI */}
        <section data-testid="section-past">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3"><Trophy className="w-5 h-5 text-[var(--gold)]" /><h2 className="heading text-4xl text-white">Tornei <span className="gold-text">Passati</span></h2></div>
            {seasons.length > 0 && (
              <div className="flex items-center gap-3">
                <select
                  data-testid="past-season-filter"
                  value={season}
                  onChange={(e) => setSeason(e.target.value)}
                  className="panel panel-gold clip-beveled-sm bg-black/60 text-white font-rajdhani uppercase tracking-wider text-sm px-4 py-2 outline-none cursor-pointer">
                  <option value="all">Tutte le stagioni</option>
                  {seasons.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      data-testid="past-prev"
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={currentPage === 0}
                      className="p-2 clip-beveled-sm border border-[var(--gold)]/40 text-[var(--gold)] hover:bg-[var(--gold)] hover:text-black transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <span className="font-rajdhani text-gray-400 text-sm tabular-nums" data-testid="past-page-indicator">{currentPage + 1}/{totalPages}</span>
                    <button
                      data-testid="past-next"
                      onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                      disabled={currentPage >= totalPages - 1}
                      className="p-2 clip-beveled-sm border border-[var(--gold)]/40 text-[var(--gold)] hover:bg-[var(--gold)] hover:text-black transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          {filteredPast.length ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="past-grid">{pageItems.map((t) => <TournamentCard key={t.id} t={t} accent="gold" />)}</div>
          ) : <p className="font-rajdhani text-gray-500">Lo storico dei tornei apparirà qui.</p>}
        </section>
      </div>
    </div>
  );
}
